import type { AxiosInstance, AxiosResponse } from 'axios';
import type { SortField } from '@/models/common';
import type { SharePermission } from '@/models/permissions';
import type { RootResponse } from '@/models/rootResponse';
import type { User, UserAccess } from '@/models/users';

export type GetUserAccesses = {
   surveyId: string;
   sort?: SortField[];
   search?: string;
};

export type RemoveUserAccess = {
   userId: string;
};

type NotifiableUsers = {
   user: User;
   is_entry_feedback_recipient: boolean;
};

class SharesService {
   private provider: AxiosInstance;

   constructor(provider: AxiosInstance) {
      this.provider = provider;
   }

   async getAccessTypes(): Promise<SharePermission[]> {
      const response: AxiosResponse<RootResponse<SharePermission[]>> =
         await this.provider.get(`/share-permissions?page=1`);

      return response.data.data;
   }

   async getUserAccesses({
      surveyId,
      sort,
      search,
   }: GetUserAccesses): Promise<UserAccess[]> {
      const response: AxiosResponse<RootResponse<UserAccess[]>> =
         await this.provider.post(`/rest/shares/search`, {
            search: {
               text: {
                  value: search ?? '',
               },
               filters: [
                  {
                     field: 'shareable_id',
                     value: surveyId,
                  },
                  {
                     field: 'shareable_type',
                     value: 'App\\Models\\Survey',
                  },
               ],
               includes: [
                  {
                     relation: 'user',
                     selects: [
                        {
                           field: 'id',
                        },
                        {
                           field: 'lastname',
                        },
                        {
                           field: 'firstname',
                        },
                     ],
                  },
                  {
                     relation: 'shareablePermission',
                  },
               ],
               ...(sort
                  ? {
                       sorts: sort
                          .filter((sort) => sort.field !== 'slug')
                          .map((sort) => ({
                             ...sort,
                             field:
                                sort.field === 'lastname' ||
                                sort.field === 'firstname'
                                   ? `user.${sort.field}.keyword`
                                   : sort.field,
                          })),
                    }
                  : {}),
            },
         });

      return response.data.data;
   }

   async removeUserAccess({ userId }: RemoveUserAccess): Promise<void> {
      await this.provider.delete(`/rest/shares`, {
         data: {
            resources: [userId],
         },
      });
   }

   async getNotifiableUsers(
      surveyId: string,
   ): Promise<RootResponse<NotifiableUsers[]>> {
      const response: AxiosResponse<RootResponse<NotifiableUsers[]>> =
         await this.provider.post(`/rest/shares/search`, {
            search: {
               includes: [
                  {
                     relation: 'user',
                     selects: [
                        {
                           field: 'id',
                        },
                        {
                           field: 'lastname',
                        },
                        {
                           field: 'firstname',
                        },
                     ],
                  },
                  {
                     relation: 'shareablePermission',
                  },
               ],
               filters: [
                  {
                     field: 'shareable_id',
                     value: surveyId,
                  },
                  {
                     field: 'shareablePermission.slug',
                     value: 'update',
                  },
                  {
                     field: 'shareable_type',
                     value: 'App\\Models\\Survey',
                  },
               ],
            },
         });

      return response.data;
   }
}

export default SharesService;
